import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TAG_META = {
  positive: { label: '🟢 Positive', dot: 'bg-green-500', chip: 'bg-green-50 text-green-700 border-green-200' },
  improvement: { label: '🟡 Improvement', dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  negative: { label: '🔴 Negative', dot: 'bg-rose-500', chip: 'bg-rose-50 text-rose-700 border-rose-200' },
};
const PLATFORM_ICON = { facebook: '📘', instagram: '📸' };
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

const Kpi = ({ label, value, color, testid }) => (
  <div className={`rounded-xl p-4 ${color}`} data-testid={testid}>
    <p className="text-2xl font-bold">{value}</p>
    <p className="text-[11px] opacity-80 mt-0.5">{label}</p>
  </div>
);

const CommentCard = ({ c, authToken, onReplied }) => {
  const [reply, setReply] = useState(c.suggested_reply || '');
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);
  const tm = TAG_META[c.tag] || TAG_META.improvement;

  const send = async () => {
    if (!reply.trim()) { toast.error('Reply खाली नहीं हो सकता'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/comments-inbox/${c.id}/reply`, {
        method: 'POST', headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: reply.trim() }),
      });
      const d = await res.json();
      if (res.ok && d.success) { toast.success('✅ Reply Meta पर post हो गया'); onReplied(c.id, reply.trim()); }
      else toast.error(d.detail || 'Reply post नहीं हुआ');
    } catch (e) { toast.error('Reply error'); }
    finally { setSending(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`comment-${c.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-lg shrink-0">{PLATFORM_ICON[c.platform] || '💬'}</span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800 truncate">{c.author}</p>
            <p className="text-[10px] text-slate-400">{fmtDate(c.created_time)}{c.is_seed ? ' · demo' : ''}</p>
          </div>
        </div>
        <span className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full border ${tm.chip}`} data-testid={`comment-tag-${c.id}`}>{tm.label}</span>
      </div>

      <p className="text-sm text-slate-700 mt-2">{c.message}</p>
      {c.post_message && <p className="text-[11px] text-slate-400 mt-1 truncate">↳ {c.post_message}</p>}
      {c.ai_summary && <p className="text-[11px] text-indigo-500 mt-1">🤖 {c.ai_summary}</p>}

      {c.replied ? (
        <div className="mt-3 bg-emerald-50 border border-emerald-100 rounded-lg p-2 text-xs text-emerald-700" data-testid={`comment-replied-${c.id}`}>
          ✅ Replied: {c.reply_text}
        </div>
      ) : !open ? (
        <button onClick={() => setOpen(true)} className="mt-3 text-xs px-3 py-1.5 rounded-lg bg-slate-800 text-white hover:bg-slate-900" data-testid={`comment-reply-open-${c.id}`}>
          ↩ Reply {c.suggested_reply ? '(AI सुझाव तैयार)' : ''}
        </button>
      ) : (
        <div className="mt-3 space-y-2">
          <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={2}
            className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg outline-none focus:border-orange-400"
            placeholder="अपना reply लिखें..." data-testid={`comment-reply-input-${c.id}`} />
          <div className="flex gap-2">
            <button onClick={send} disabled={sending} className="text-xs px-3 py-1.5 rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50" data-testid={`comment-reply-send-${c.id}`}>
              {sending ? '⏳...' : '📤 Send to Meta'}
            </button>
            {c.suggested_reply && (
              <button onClick={() => setReply(c.suggested_reply)} className="text-xs px-3 py-1.5 rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-50" data-testid={`comment-use-ai-${c.id}`}>
                🤖 AI reply भरें
              </button>
            )}
            <button onClick={() => setOpen(false)} className="text-xs px-3 py-1.5 rounded-lg text-slate-400">रद्द</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function CommentsInboxPanel({ token }) {
  const authToken = token || localStorage.getItem('token');
  const [stats, setStats] = useState({});
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [tag, setTag] = useState('all');
  const [platform, setPlatform] = useState('all');
  const [status, setStatus] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, cRes] = await Promise.all([
        fetch(`${API_URL}/api/comments-inbox/stats`, { headers: { Authorization: `Bearer ${authToken}` } }),
        fetch(`${API_URL}/api/comments-inbox/?tag=${tag}&platform=${platform}&status=${status}`, { headers: { Authorization: `Bearer ${authToken}` } }),
      ]);
      const s = await sRes.json(); const c = await cRes.json();
      setStats(s.stats || {});
      setComments(c.comments || []);
    } catch (e) { toast.error('Comments load नहीं हुए'); }
    finally { setLoading(false); }
  }, [authToken, tag, platform, status]);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/comments-inbox/sync`, { method: 'POST', headers: { Authorization: `Bearer ${authToken}` } });
      const d = await res.json();
      if (d.success) toast.success(`🔄 Sync: ${d.new} नए comments · ${d.tagged} AI-tagged`);
      else toast.error('Sync विफल');
      load();
    } catch (e) { toast.error('Sync error'); }
    finally { setSyncing(false); }
  };

  const onReplied = (id, text) => setComments(prev => prev.map(c => c.id === id ? { ...c, replied: true, reply_text: text } : c));

  const FilterPills = ({ value, set, options }) => (
    <div className="flex gap-1.5 flex-wrap">
      {options.map(o => (
        <button key={o.id} onClick={() => set(o.id)}
          className={`px-2.5 py-1 rounded-full text-xs font-medium border ${value === o.id ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-600 border-slate-200 hover:border-orange-300'}`}
          data-testid={`filter-${o.id}`}>{o.label}</button>
      ))}
    </div>
  );

  return (
    <div className="space-y-5" data-testid="comments-inbox-panel">
      <div className="bg-gradient-to-r from-fuchsia-600 to-pink-600 rounded-2xl p-5 text-white flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold">💬 Comments Inbox</h2>
          <p className="text-sm text-white/90 mt-1">Facebook + Instagram के comments — AI auto-tagging के साथ, सीधे reply करें।</p>
        </div>
        <button onClick={sync} disabled={syncing} className="px-4 py-2 rounded-xl bg-white/15 hover:bg-white/25 text-sm font-semibold disabled:opacity-50" data-testid="comments-sync-btn">
          {syncing ? '⏳ Sync हो रहा है...' : '🔄 Sync Comments'}
        </button>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
        <Kpi label="कुल" value={stats.total || 0} color="bg-slate-100 text-slate-700" testid="kpi-total" />
        <Kpi label="🟢 Positive" value={stats.positive || 0} color="bg-green-50 text-green-700" testid="kpi-positive" />
        <Kpi label="🟡 Improvement" value={stats.improvement || 0} color="bg-amber-50 text-amber-700" testid="kpi-improvement" />
        <Kpi label="🔴 Negative" value={stats.negative || 0} color="bg-rose-50 text-rose-700" testid="kpi-negative" />
        <Kpi label="Unreplied" value={stats.unreplied || 0} color="bg-indigo-50 text-indigo-700" testid="kpi-unreplied" />
        <Kpi label="📘 FB / 📸 IG" value={`${stats.facebook || 0}/${stats.instagram || 0}`} color="bg-fuchsia-50 text-fuchsia-700" testid="kpi-platforms" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <FilterPills value={tag} set={setTag} options={[
          { id: 'all', label: 'सभी Tags' }, { id: 'positive', label: '🟢 Positive' }, { id: 'improvement', label: '🟡 Improvement' }, { id: 'negative', label: '🔴 Negative' },
        ]} />
        <FilterPills value={platform} set={setPlatform} options={[
          { id: 'all', label: 'सभी' }, { id: 'facebook', label: '📘 FB' }, { id: 'instagram', label: '📸 IG' },
        ]} />
        <FilterPills value={status} set={setStatus} options={[
          { id: 'all', label: 'All' }, { id: 'unreplied', label: 'Unreplied' }, { id: 'replied', label: 'Replied' },
        ]} />
      </div>

      {loading ? (
        <p className="text-center text-slate-400 py-10" data-testid="comments-loading">⏳ लोड हो रहा है...</p>
      ) : comments.length === 0 ? (
        <div className="text-center py-12 text-slate-400" data-testid="comments-empty">
          <p className="text-4xl mb-3">💬</p>
          <p>कोई comment नहीं मिला। ऊपर "Sync Comments" दबाएँ ताकि Meta से नए comments आएँ।</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="comments-list">
          {comments.map(c => <CommentCard key={c.id} c={c} authToken={authToken} onReplied={onReplied} />)}
        </div>
      )}
    </div>
  );
}
